/**
 * Shared presentation and interaction constants for every Heglo web surface.
 * Values live here so changes are intentional, named, and applied consistently.
 */
export const UI_TIMING = Object.freeze({
  tooltipDelayMs: 250,
  shortTransitionMs: 200,
  standardTransitionMs: 300,
  queryFeedbackDelayMs: 500,
  tenantClockRefreshMs: 30_000,
  postSuccessRedirectMs: 2_000,
  toastDurationMs: 3_000,
} as const);

export const UI_LIMITS = Object.freeze({
  maximumFirebaseTesters: 50,
  maximumReleaseNotesLength: 500,
  maximumTooltipWidthRem: 20,
  defaultTableLoadingRows: 5,
  maximumVisiblePaginationNeighbours: 2,
  defaultImageUploadMb: 5,
} as const);

export const TIME_INTERVALS = Object.freeze({
  minuteMs: 60_000,
  hourMs: 3_600_000,
  dayMs: 86_400_000,
  relativeDateCutoffDays: 7,
} as const);

export const ADMIN_LAYOUT = Object.freeze({
  sidebarWidthPx: 270,
  headerHeightPx: 60,
  contentMaxWidthPx: 1280,
  mobileNavigationOffsetPx: 80,
} as const);

export const SIDEBAR_DEFAULTS = Object.freeze({
  cookieName: 'sidebar_state',
  cookieMaxAgeSeconds: 60 * 60 * 24 * 7,
  desktopWidth: '16rem',
  mobileWidth: '18rem',
  iconWidth: '3rem',
  keyboardShortcut: 'b',
  skeletonWidth: '70%',
} as const);

export const PORTAL_STORAGE_KEYS = Object.freeze({
  uiSettings: 'heglo-ui-settings',
  legacyUiSettings: 'heglo-ui-settings',
} as const);

export const BRANDING_DEFAULTS = Object.freeze({
  colorInput: '#000000',
  hexColorPlaceholder: '#RRGGBB',
} as const);

/** Concrete values are centralized for browser APIs that cannot resolve theme classes. */
export const SEMANTIC_COLOR_VALUES = Object.freeze({
  primary: '#3b82f6',
  success: '#22c55e',
  warning: '#f59e0b',
  info: '#8b5cf6',
  destructive: '#ef4444',
  muted: '#9ca3af',
  foreground: '#111827',
  background: '#ffffff',
} as const);

export const SEMANTIC_CSS_COLORS = Object.freeze({
  primary: 'hsl(var(--primary))',
  success: 'hsl(var(--success))',
  warning: 'hsl(var(--warning))',
  info: 'hsl(var(--info))',
  destructive: 'hsl(var(--destructive))',
  muted: 'hsl(var(--muted-foreground))',
  foreground: 'hsl(var(--foreground))',
  background: 'hsl(var(--background))',
} as const);

export const PROVIDER_BRANDING_DEFAULTS = Object.freeze({
  primaryColor: '#0f172a',
  secondaryColor: '#f8fafc',
  accentColor: '#d97706',
  textOnPrimary: '#ffffff',
  navigationBackground: '#ffffff',
  navigationText: '#0f172a',
} as const);

export const MAP_PRESENTATION = Object.freeze({
  markerSizePx: 20,
  providerMarkerSizePx: 24,
  workerMarkerSizePx: 26,
  workerMarkerFontSizePx: 11,
  orderMarkerSizePx: 22,
  orderMarkerRadiusPx: 4,
  orderMarkerRotationDegrees: 45,
  locationMarkerSizePx: 16,
  markerBorderPx: 3,
  markerRadiusPercent: 50,
  markerShadow: '0 2px 6px rgb(0 0 0 / 30%)',
  locationMarkerRingPx: 3,
  zoneStrokeWidth: 2,
  zoneFillOpacity: 0.08,
  drawingFillOpacity: 0.3,
  kilometerInMeters: 1_000,
  defaultRadiusKm: 10,
  nearbyDefaultRadiusKm: 5,
  radiusOptionsKm: [1, 2, 5, 10, 20, 50] as const,
  defaultZoom: 12,
  nearbyZoom: 13,
  mapMinimumHeightPx: 400,
} as const);

export const COUNTRY_MAP_CENTERS: Readonly<Record<string, readonly [number, number]>> = Object.freeze({
  SD: [15.5007, 32.5599],
  SA: [24.7136, 46.6753],
});

export const DEFAULT_MAP_CENTER: readonly [number, number] = Object.freeze([0, 0]);

export function getCountryMapCenter(countryCode?: string | null): [number, number] {
  const center = COUNTRY_MAP_CENTERS[countryCode?.trim().toUpperCase() || ''] || DEFAULT_MAP_CENTER;
  return [center[0], center[1]];
}

export function createMapMarkerHtml(color: string, sizePx: number = MAP_PRESENTATION.markerSizePx, withRing = false): string {
  const ring = withRing ? `0 0 0 ${MAP_PRESENTATION.locationMarkerRingPx}px ${color},` : '';
  return `<div style="background:${color};width:${sizePx}px;height:${sizePx}px;border-radius:${MAP_PRESENTATION.markerRadiusPercent}%;border:${MAP_PRESENTATION.markerBorderPx}px solid ${SEMANTIC_COLOR_VALUES.background};box-shadow:${ring}${MAP_PRESENTATION.markerShadow};"></div>`;
}

export const ORDER_STATUS_COLORS = Object.freeze({
  pending: SEMANTIC_CSS_COLORS.primary,
  assigned: SEMANTIC_CSS_COLORS.warning,
  started: SEMANTIC_CSS_COLORS.info,
  inProgress: 'hsl(var(--chart-2))',
  completed: SEMANTIC_CSS_COLORS.success,
  cancelled: SEMANTIC_CSS_COLORS.destructive,
  fallback: SEMANTIC_CSS_COLORS.muted,
} as const);

export const REPORT_HEATMAP = Object.freeze({
  thresholds: [0, 0.2, 0.4, 0.6, 0.8] as const,
  colors: [
    SEMANTIC_CSS_COLORS.muted,
    'hsl(var(--chart-1))',
    SEMANTIC_CSS_COLORS.success,
    SEMANTIC_CSS_COLORS.warning,
    'hsl(var(--chart-5))',
    SEMANTIC_CSS_COLORS.destructive,
  ] as const,
} as const);

export const WYSIWYG_PRESENTATION = Object.freeze({
  selectionBackground: '#e3f2fd',
} as const);

export const IMAGE_VIEWER_DEFAULTS = Object.freeze({
  initialScale: 1,
  minimumScale: 0.5,
  maximumScale: 3,
  zoomStep: 0.25,
  rotationStepDegrees: 90,
  percentMultiplier: 100,
} as const);

export const LANDING_THEME_STYLES = Object.freeze({
  modern: { background: '#f7fbff', foreground: '#10243e', accent: '#1673ea', previewClass: 'bg-gradient-to-br from-sky-100 to-blue-600' },
  elegant: { background: '#fbf7f2', foreground: '#30241d', accent: '#9b6b43', previewClass: 'bg-gradient-to-br from-stone-100 to-amber-800' },
  bold: { background: '#0b1020', foreground: '#ffffff', accent: '#ffb000', previewClass: 'bg-gradient-to-br from-slate-950 to-amber-400' },
  minimal: { background: '#f8f8f6', foreground: '#171717', accent: '#333333', previewClass: 'bg-gradient-to-br from-white to-slate-300' },
  vibrant: { background: '#fff7ed', foreground: '#26183b', accent: '#a21caf', previewClass: 'bg-gradient-to-br from-fuchsia-500 via-orange-300 to-cyan-400' },
} as const);

export const SUPPORTED_PORTAL_LOCALES = Object.freeze(['ar', 'en'] as const);
export type SupportedPortalLocale = (typeof SUPPORTED_PORTAL_LOCALES)[number];

export const TENANT_TIME_FORMATS = Object.freeze(['12h', '24h'] as const);
export type TenantTimeFormat = (typeof TENANT_TIME_FORMATS)[number];

export const DEFAULT_TENANT_PRESENTATION = Object.freeze({
  enabledLocales: SUPPORTED_PORTAL_LOCALES,
  defaultLocale: 'ar' as SupportedPortalLocale,
  timeFormat: '12h' as TenantTimeFormat,
} as const);

/** Neutral formatting fallbacks used only before tenant metadata is available. */
export const GLOBAL_FORMAT_DEFAULTS = Object.freeze({
  currency: 'USD',
  timezone: 'UTC',
  phonePrefix: '',
} as const);

export function countryCodeToFlag(countryCode: string): string {
  const normalized = countryCode.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(normalized)) return '';
  return String.fromCodePoint(...[...normalized].map((character) => 127397 + character.charCodeAt(0)));
}

export function formatCountryName(
  countryCode: string,
  locale: SupportedPortalLocale,
  fallback = '',
): string {
  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(countryCode.toUpperCase()) || fallback;
  } catch {
    return fallback;
  }
}

export function normalizeTenantTimeFormat(value: unknown): TenantTimeFormat {
  return value === '24h' ? '24h' : '12h';
}

export function formatTenantClock(
  date: Date,
  locale: SupportedPortalLocale,
  timezone: string,
  timeFormat: TenantTimeFormat,
): string {
  try {
    return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-SA' : 'en-US', { numberingSystem: 'latn',
      timeZone: timezone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: timeFormat === '12h',
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-SA' : 'en-US', { numberingSystem: 'latn',
      hour: 'numeric',
      minute: '2-digit',
      hour12: timeFormat === '12h',
    }).format(date);
  }
}
